import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { showAlert } from "@/lib/alert";
import { normalizeImageAsset, pickImageFromCamera } from "@/lib/image-picker";
import {
  INSPECTION_ANGLES,
  InspectionAngleValue,
} from "@/lib/inspection-tier-requirements";
import { MAX_UPLOAD_SIZE } from "@/lib/validation/file";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Image } from "expo-image";
import React, { useState } from "react";
import {
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { BrandColors, Colors } from "../../constants/theme";
import { ThemedText } from "../themed-text";
import { CapturedMedia, validateMediaSize } from "./media-capture";

const CAR_ANGLES_IMAGE = require("@/assets/images/inspection-car-angles.png");
const CAR_ANGLES_ASPECT_RATIO = 440 / 710;

/** Marker centres as a percentage of the hint image, matching where each view is drawn. */
const MARKER_POSITIONS: Record<
  InspectionAngleValue,
  { left: `${number}%`; top: `${number}%` }
> = {
  front: { left: "50%", top: "8%" },
  rear: { left: "50%", top: "91%" },
  left: { left: "11%", top: "50%" },
  right: { left: "89%", top: "50%" },
};

const MARKER_SIZE = 28;

interface AnglePhotoCaptureProps {
  media: CapturedMedia[];
  onMediaAdded: (media: CapturedMedia) => void;
  onMediaRemoved: (id: string) => void;
  subtitle?: string;
}

export function AnglePhotoCapture({
  media,
  onMediaAdded,
  onMediaRemoved,
  subtitle,
}: AnglePhotoCaptureProps) {
  const [capturingAngle, setCapturingAngle] =
    useState<InspectionAngleValue | null>(null);
  const desktop = useDesktopLayout();
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const isDark = colorScheme === "dark";

  const cardBackground = isDark ? "#1a2332" : BrandColors.white;
  const slotBackground = isDark
    ? "rgba(15, 185, 177, 0.08)"
    : BrandColors.primaryLight;

  const photoForAngle = (angle: InspectionAngleValue) =>
    media.find((item) => item.type === "photo" && item.angle === angle);

  const capturedCount = INSPECTION_ANGLES.filter((angle) =>
    photoForAngle(angle.value),
  ).length;

  const capturePhoto = async (angle: InspectionAngleValue) => {
    try {
      setCapturingAngle(angle);
      const asset = await pickImageFromCamera(
        { allowsEditing: false },
        true,
        "We need camera access to take photos.",
      );

      if (!asset) {
        return;
      }

      const normalizedAsset = await normalizeImageAsset(asset);
      const { isValid, message } = await validateMediaSize(
        normalizedAsset,
        MAX_UPLOAD_SIZE,
      );

      if (!isValid) {
        showAlert("File Too Large", message ?? "This file is too large.");
        return;
      }

      const existing = photoForAngle(angle);
      if (existing) {
        onMediaRemoved(existing.id);
      }

      onMediaAdded({
        uri: normalizedAsset.uri,
        type: "photo",
        id: `${Date.now()}-${Math.random()}`,
        angle,
      });
    } catch (error) {
      console.error("Error taking angle photo:", error);
      showAlert("Error", "Failed to take photo");
    } finally {
      setCapturingAngle(null);
    }
  };

  const handleAnglePress = (angle: InspectionAngleValue, label: string) => {
    const existing = photoForAngle(angle);

    if (!existing) {
      capturePhoto(angle);
      return;
    }

    showAlert(label, "Retake or remove this photo?", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Remove",
        style: "destructive",
        onPress: () => onMediaRemoved(existing.id),
      },
      { text: "Retake", onPress: () => capturePhoto(angle) },
    ]);
  };

  return (
    <View
      style={[
        styles.container,
        desktop && styles.desktopContainer,
        { backgroundColor: cardBackground, borderColor: theme.border },
      ]}
    >
      <View style={styles.header}>
        <View style={styles.headerText}>
          <ThemedText style={styles.title} type="smallSemiBold">
            Vehicle angles
          </ThemedText>
          <ThemedText
            style={[styles.subtitle, { color: theme.icon }]}
            type="extraSmall"
          >
            {subtitle ??
              "Take a photo of the front, rear, left and right of the vehicle."}
          </ThemedText>
        </View>
        <ThemedText
          style={[styles.counterText, { color: theme.icon }]}
          type="extraSmall"
        >
          {capturedCount}/{INSPECTION_ANGLES.length}
        </ThemedText>
      </View>

      <View style={styles.hintWrapper}>
        <View style={styles.hint}>
          <Image
            source={CAR_ANGLES_IMAGE}
            style={styles.hintImage}
            contentFit="contain"
          />
          {INSPECTION_ANGLES.map((angle) => {
            const captured = !!photoForAngle(angle.value);

            return (
              <TouchableOpacity
                key={angle.value}
                accessibilityLabel={`${angle.label} photo`}
                style={[
                  styles.marker,
                  MARKER_POSITIONS[angle.value],
                  {
                    backgroundColor: captured
                      ? BrandColors.success
                      : BrandColors.mediumGray,
                  },
                ]}
                onPress={() => handleAnglePress(angle.value, angle.label)}
                disabled={capturingAngle !== null}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons
                  name={captured ? "checkmark" : "camera"}
                  size={14}
                  color={BrandColors.white}
                />
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      <View style={styles.slots}>
        {INSPECTION_ANGLES.map((angle) => {
          const photo = photoForAngle(angle.value);
          const isCapturing = capturingAngle === angle.value;

          return (
            <TouchableOpacity
              key={angle.value}
              style={[
                styles.slot,
                {
                  backgroundColor: slotBackground,
                  borderColor: photo
                    ? BrandColors.success
                    : BrandColors.primary,
                },
              ]}
              onPress={() => handleAnglePress(angle.value, angle.label)}
              disabled={capturingAngle !== null}
              activeOpacity={0.7}
            >
              {photo ? (
                <Image
                  source={{ uri: photo.uri }}
                  style={styles.slotImage}
                  contentFit="cover"
                />
              ) : isCapturing ? (
                <ActivityIndicator size="small" color={BrandColors.primary} />
              ) : (
                <Ionicons
                  name="camera-outline"
                  size={24}
                  color={BrandColors.primary}
                />
              )}
              <View
                style={[styles.slotLabel, photo && styles.slotLabelOverlay]}
              >
                {photo ? (
                  <Ionicons
                    name="checkmark-circle"
                    size={14}
                    color={BrandColors.success}
                  />
                ) : null}
                <ThemedText
                  style={[
                    styles.slotLabelText,
                    {
                      color: photo ? BrandColors.white : BrandColors.primary,
                    },
                  ]}
                  type="extraSmallSemiBold"
                >
                  {angle.label}
                </ThemedText>
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginHorizontal: 16,
    marginBottom: 16,
  },
  desktopContainer: {
    marginHorizontal: 0,
    marginBottom: 20,
    borderRadius: 16,
    padding: 20,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
  },
  headerText: {
    flex: 1,
    gap: 4,
  },
  title: {
    fontSize: 16,
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 16,
  },
  counterText: {
    fontSize: 12,
  },
  hintWrapper: {
    alignItems: "center",
    marginBottom: 16,
  },
  hint: {
    width: "70%",
    maxWidth: 260,
    aspectRatio: CAR_ANGLES_ASPECT_RATIO,
    backgroundColor: BrandColors.white,
    borderRadius: 12,
    overflow: "hidden",
  },
  hintImage: {
    width: "100%",
    height: "100%",
  },
  marker: {
    position: "absolute",
    width: MARKER_SIZE,
    height: MARKER_SIZE,
    borderRadius: MARKER_SIZE / 2,
    marginLeft: -MARKER_SIZE / 2,
    marginTop: -MARKER_SIZE / 2,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderColor: BrandColors.white,
  },
  slots: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  slot: {
    width: "47%",
    flexGrow: 1,
    aspectRatio: 4 / 3,
    borderWidth: 1.5,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    overflow: "hidden",
  },
  slotImage: {
    ...StyleSheet.absoluteFillObject,
  },
  slotLabel: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  slotLabelOverlay: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    paddingVertical: 4,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  slotLabelText: {
    fontSize: 11,
  },
});
